import { useState } from 'react';
import { Sun, Calendar, CalendarDays, CalendarRange, TrendingUp, TrendingDown, CheckCircle2, XCircle, Clock } from 'lucide-react';

const PERIODS = [
  { key: 'today', label: "Today's Final", icon: Sun },
  { key: 'week',  label: 'This Week',     icon: Calendar },
  { key: 'month', label: 'This Month',    icon: CalendarDays },
  { key: 'year',  label: 'This Year',     icon: CalendarRange },
];

const ROWS = {
  today: [
    { sym: 'MU',   co: 'Micron Technology',  dir: 'up', range: '$782 – $798', actual: '$785.20', chg: '+1.44%', conf: 78, verdict: 'hit' },
    { sym: 'NVDA', co: 'NVIDIA',             dir: 'up', range: '$905 – $940', actual: '$892.41', chg: '+2.31%', conf: 85, verdict: 'miss' },
    { sym: 'LRCX', co: 'Lam Research',       dir: 'up', range: '$920 – $945', actual: '$912.30', chg: '+2.10%', conf: 71, verdict: 'miss' },
    { sym: 'ALAB', co: 'Astera Labs',        dir: 'dn', range: '$85 – $91',   actual: '$88.45',  chg: '−0.55%', conf: 62, verdict: 'hit' },
    { sym: 'WDC',  co: 'Western Digital',    dir: 'up', range: '$62 – $68',   actual: null,      chg: null,     conf: 58, verdict: 'pend' },
  ],
  week: [
    { sym: 'MU',   co: 'Micron Technology',  dir: 'up', range: '$790 – $820', actual: '$785.20', chg: '+1.44%', conf: 70, verdict: 'miss' },
    { sym: 'SNDK', co: 'SanDisk',            dir: 'up', range: '$1540 – $1590', actual: null,    chg: null,     conf: 65, verdict: 'pend' },
    { sym: 'INTC', co: 'Intel',              dir: 'dn', range: '$28 – $31',   actual: '$29.80',  chg: '+0.7%',  conf: 60, verdict: 'hit' },
  ],
  month: [
    { sym: 'MU',   co: 'Micron Technology',  dir: 'up', range: '$800 – $860', actual: null, chg: null, conf: 65, verdict: 'pend' },
    { sym: 'NVDA', co: 'NVIDIA',             dir: 'up', range: '$920 – $980', actual: null, chg: null, conf: 72, verdict: 'pend' },
  ],
  year: [
    { sym: 'MU',   co: 'Micron Technology',  dir: 'up', range: '$900 – $1,100', actual: null, chg: null, conf: 55, verdict: 'pend' },
    { sym: 'LRCX', co: 'Lam Research',       dir: 'up', range: '$1,050 – $1,250', actual: null, chg: null, conf: 50, verdict: 'pend' },
  ],
};

const VERDICT_CONF  = { hit: 'cf-hit', beat: 'cf-beat', miss: 'cf-miss', pend: 'cf-pend' };
const VERDICT_BADGE = { hit: 'b-hit', beat: 'b-beat', miss: 'b-miss', pend: 'b-pend' };
const VERDICT_LABEL = { hit: 'Hit', beat: 'Beat', miss: 'Miss', pend: 'Pending' };

export default function ProjectionEngine() {
  const [active, setActive] = useState('today');
  const rows = ROWS[active];

  return (
    <section className="section" id="projections">
      <div className="wrap">
        <div className="sec-tag"><CalendarRange size={13} />Projection Engine</div>
        <h2>Four periods.<br /><em>One accountability.</em></h2>
        <p className="section-lead">
          Every day we publish price projection ranges for Today, This Week, This Month, and This Year.
          When the period closes, the outcome is locked — Hit, Beat, or Miss — and explained publicly.
        </p>

        <div className="period-chips">
          {PERIODS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              className={`period-chip ${active === key ? 'active' : ''}`}
              onClick={() => setActive(key)}
            >
              <Icon size={14} />{label}
            </button>
          ))}
        </div>

        <div className="ptbl-wrap">
          <table className="ptbl">
            <thead>
              <tr>
                <th>Stock</th>
                <th>Direction</th>
                <th>Actual</th>
                <th>Verdict</th>
                <th>Confidence</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.sym}>
                  <td>
                    <div className="pt-sym">{r.sym}</div>
                    <div className="pt-co">{r.co}</div>
                  </td>
                  <td>
                    <div className={`pt-dir ${r.dir === 'up' ? 'up' : 'dn'}`}>
                      {r.dir === 'up' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                      {r.dir === 'up' ? 'Bullish' : 'Bearish'}
                    </div>
                    <div className="pt-range">{r.range}</div>
                  </td>
                  <td>
                    {r.actual
                      ? <><div className="pt-actual">{r.actual}</div><div className={`pt-chg ${r.chg?.startsWith('+') ? 'up' : 'dn'}`}>{r.chg}</div></>
                      : <span style={{ color: 'var(--muted)', fontSize: 13 }}>—</span>}
                  </td>
                  <td>
                    <span className={`badge ${VERDICT_BADGE[r.verdict]}`}>
                      {r.verdict === 'hit'  && <CheckCircle2 size={9} />}
                      {r.verdict === 'beat' && <TrendingUp size={9} />}
                      {r.verdict === 'miss' && <XCircle size={9} />}
                      {r.verdict === 'pend' && <Clock size={9} />}
                      {VERDICT_LABEL[r.verdict]}
                    </span>
                  </td>
                  <td>
                    <div className="conf-row">
                      <div className="conf-bar">
                        <div className={`conf-fill ${VERDICT_CONF[r.verdict]}`} style={{ width: `${r.conf}%` }} />
                      </div>
                      <span className="conf-pct">{r.conf}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="ptbl-legend">
            {['hit','beat','miss','pend'].map(v => (
              <div key={v} className="ptbl-legend-item">
                <span className={`badge ${VERDICT_BADGE[v]}`}>{VERDICT_LABEL[v]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
